<template>
  <div class="tab-content">
    <div class="app-download-section">
      <div class="hiking-image d-none d-md-block">
        <img :src="hikingImage1" alt="Hikers on trail" />
      </div>

      <div class="export-options">
        <h2 class="display-5"><strong>Export the route as a file</strong></h2>

        <div class="file-format-options">
          <button
            v-if="isUserLoggedIn"
            v-for="format in availableFormats"
            :key="format.id"
            class="format-option"
            @click="handleExportFormat(format.id)"
            :disabled="!!downloadingFormatId" >
            <span>{{ format.name }}</span>
            <span class="d-flex align-items-center">
              <span v-if="downloadingFormatId === format.id" class="spinner"></span>
              <span v-else class="material-icons text-yellow">arrow_circle_down</span>
            </span>
          </button>

          <button
            v-else
            v-for="format in availableFormats"
            :key="format.id"
            class="format-option"
            @click="handleLogin"
          >
            <span>{{ format.name }}</span>
            <span class="d-flex align-items-center">
              <span class="mr-1" style="width: 95px; line-height: 1;"><small class="text-muted" style="font-size: .75em; text-wrap: auto;">Sign in to download</small></span>
              <span class="material-icons text-muted">arrow_circle_down</span>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import hikingImage1 from '../../images/download-modal-2.webp';
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import { getDownloadResourceContext, getResourceNoun } from './context';
import graph_client from '../../utils/graphql/graph_client';
import { GetHike } from "../../utils/graphql/hikes-graph";
import { geojsonToGpx } from "../../utils/geojsonToGpx";

// Props
const props = defineProps<{
  userData: any;
}>();

const emit = defineEmits<{
  close: [];
}>();

// Data
const downloadingFormatId = ref<ExportFormat | null>(null);

type ExportFormat = 'gpx' | 'gpx_garmin' | 'kml' | 'geojson';

const formatConfig: Record<ExportFormat, { name: string; endpoint: string; mimeType: string; extension: string }> = {
  gpx: {
    name: 'GPX',
    endpoint: 'gpx_file',
    mimeType: 'application/gpx+xml',
    extension: 'gpx'
  },
  gpx_garmin: {
    name: 'GPX (Garmin Compatible)',
    endpoint: 'gpx_file',
    mimeType: 'application/gpx+xml',
    extension: 'gpx'
  },
  kml: {
    name: 'KML (Google Earth)',
    endpoint: 'kml_file',
    mimeType: 'application/vnd.google-earth.kml+xml',
    extension: 'kml'
  },
  geojson: {
    name: 'GeoJSON',
    endpoint: 'geojson_file',
    mimeType: 'application/geo+json',
    extension: 'geojson'
  }
};

const fileFormats = Object.entries(formatConfig).map(([id, value]) => ({
  id: id as ExportFormat,
  name: value.name
}));

const isUserLoggedIn = computed(() => props.userData != null);

const resourceContext = computed(() => getDownloadResourceContext());
const isRecording = computed(() => resourceContext.value.graphqlType === 'Recording');
const resourceNoun = computed(() => getResourceNoun(resourceContext.value.graphqlType));

const availableFormats = computed(() =>
  // The Garmin-compatible GPX merges the hike's route-style ptv2s into a single
  // <rte> client-side, so it only applies to the hike/trail GraphQL path.
  isRecording.value ? fileFormats.filter(format => format.id !== 'gpx_garmin') : fileFormats
);

const getTrailUrl = () => {
  if (resourceContext.value.downloadPath) {
    return resourceContext.value.downloadPath;
  }
  if (window.country_slug && window.region_slug && window.slug) {
    return `/hikes/${window.country_slug}/${window.region_slug}/${window.slug}`;
  }
  const pathMatch = window.location.pathname.match(/\/hikes\/([^\/]+)\/([^\/]+)\/([^\/]+)/);
  if (pathMatch) {
    return `/hikes/${pathMatch[1]}/${pathMatch[2]}/${pathMatch[3]}`;
  }
  return window.location.pathname;
};

const getRecordingUrl = () => {
  const context = resourceContext.value;
  if (context.downloadPath) return context.downloadPath;

  const match = window.location.pathname.match(/\/recordings\/[^/]+/);
  return match ? match[0] : window.location.pathname;
};

const buildDownloadUrl = (formatId: ExportFormat) => {
  const endpoint = formatConfig[formatId]?.endpoint || formatConfig.gpx.endpoint;
  const basePath = isRecording.value ? getRecordingUrl() : getTrailUrl();
  const sanitizedBase = basePath.replace(/\/(gpx_file|kml_file|geojson_file)(\/)?$/, '');
  return `${sanitizedBase}/${endpoint}`;
};

const slugify = (value: string) => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
};

const extractFilenameFromHeader = (disposition: string | null): string | null => {
  if (!disposition) return null;

  // Matches both filename="name.ext" and filename*=utf-8''name.ext
  const filenameStarMatch = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (filenameStarMatch && filenameStarMatch[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1].replace(/["']/g, '').trim());
    } catch (_) {
      return filenameStarMatch[1].replace(/["']/g, '').trim();
    }
  }

  const filenameMatch = disposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
  return filenameMatch ? filenameMatch[2].trim() : null;
};

const buildFallbackFilename = (formatId: ExportFormat) => {
  const extension = formatConfig[formatId]?.extension || formatConfig.gpx.extension;
  const nameSource = resourceContext.value.name || resourceNoun.value;
  const slug = slugify(nameSource || resourceNoun.value) || 'hiiker-trail';
  return `${slug}.${extension}`;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

type HikeLike = {
  id?: string | number;
  name?: string;
  title?: string;
  slug?: string;
  createdAt?: string;
  geojsonRoutesOnlyString?: any;
  geojsonMultilinestringsWithStartEndPointsString?: any;
};

const parseHikeGeojson = (geojsonLike: any) => {
  if (!geojsonLike) return null;

  if (typeof geojsonLike === 'string') {
    try {
      return JSON.parse(geojsonLike);
    } catch (error) {
      console.error('Failed to parse hike geojson string', error);
      return null;
    }
  }

  if (geojsonLike?.type === 'FeatureCollection') {
    return geojsonLike;
  }

  return null;
};

const collectLineFeaturesFromGeojson = (geojson: any) => {
  if (!geojson || !Array.isArray(geojson.features)) {
    return [];
  }

  const results: any[] = [];

  geojson.features.forEach((feature: any) => {
    const geometry = feature?.geometry;
    if (!geometry) return;

    if (geometry.type === 'LineString') {
      const coords = geometry.coordinates;
      if (Array.isArray(coords) && coords.length > 1) {
        results.push({
          type: 'Feature',
          properties: feature.properties ? { ...feature.properties } : {},
          geometry: {
            type: 'LineString',
            coordinates: coords
          }
        });
      }
      return;
    }

    if (geometry.type === 'MultiLineString') {
      const lines = geometry.coordinates || [];
      lines.forEach((line: any) => {
        if (Array.isArray(line) && line.length > 1) {
          results.push({
            type: 'Feature',
            properties: feature.properties ? { ...feature.properties } : {},
            geometry: {
              type: 'LineString',
              coordinates: line
            }
          });
        }
      });
    }
  });

  return results;
};

const composeGpxFileName = (label: string) => {
  const base = (label || 'hiiker').toString();
  const slug = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'hiiker';

  return `${slug}.gpx`;
};

const buildHikeGpxContext = (hike: HikeLike, { routesOnly = false } = {}) => {
  const metadata: any = {
    creator: 'Hiiker',
    metadata: {}
  };

  const title = hike?.name || hike?.title || hike?.slug;
  if (title) metadata.metadata.name = title;
  if (hike?.createdAt) metadata.metadata.time = hike.createdAt;

  // The Garmin-compatible export uses geojsonRoutesOnlyString, which the backend
  // builds from only the 'route' style ptv2s (no alts/spurs/transport legs).
  const geojson = routesOnly
    ? parseHikeGeojson(hike?.geojsonRoutesOnlyString)
    : parseHikeGeojson(
      hike?.geojsonMultilinestringsWithStartEndPointsString ||
      hike?.geojsonRoutesOnlyString
    );
  const lineFeatures = collectLineFeaturesFromGeojson(geojson);

  return {
    lineFeatures,
    metadata,
    fileName: composeGpxFileName(title || 'hike')
  };
};

const kmzSafe = (value: string) => {
  if (!value) return '';
  return value.replace(/[&<>'"]/g, (char) => {
    switch (char) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return char;
    }
  });
};

const lineFeaturesToKml = (lineFeatures: any[], documentName: string) => {
  const header = `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<kml xmlns="http://www.opengis.net/kml/2.2">\n` +
    `<Document>\n` +
    `<name>${kmzSafe(documentName)}</name>\n`;

  const placemarks = lineFeatures.map((feature, index) => {
    const coords = Array.isArray(feature?.geometry?.coordinates) ? feature.geometry.coordinates : [];
    if (!coords.length) return '';
    const coordinateString = coords.map((coord: number[]) => {
      const [lng, lat, ele] = coord;
      const elevation = Number.isFinite(ele) ? ele : 0;
      return `${lng},${lat},${elevation}`;
    }).join(' ');
    const name = feature?.properties?.name || `${documentName || 'Trail'} Segment ${index + 1}`;
    return (
      `<Placemark>\n` +
      `  <name>${kmzSafe(name)}</name>\n` +
      `  <LineString>\n` +
      `    <tessellate>1</tessellate>\n` +
      `    <coordinates>${coordinateString}</coordinates>\n` +
      `  </LineString>\n` +
      `</Placemark>\n`
    );
  }).join('');

  const footer = `</Document>\n</kml>`;
  return `${header}${placemarks}${footer}`;
};

const downloadTrailViaGraphql = async (formatId: ExportFormat) => {
  const trailId = resourceContext.value.id;
  if (!trailId) {
    throw new Error('No trail selected for download');
  }

  const response = await graph_client.request(GetHike, { id: trailId });
  const hike = response?.hike;
  if (!hike) {
    throw new Error('Hike not found');
  }

  const routesOnly = formatId === 'gpx_garmin';
  const { lineFeatures = [], metadata, fileName } = buildHikeGpxContext(hike, { routesOnly });
  if (!lineFeatures.length) {
    window.alert(`No ${formatConfig[formatId].name} data is available for this trail yet.`);
    return;
  }

  const featureCollection = {
    type: 'FeatureCollection',
    features: lineFeatures
  };

  if (formatId === 'gpx_garmin') {
    // Collapse every route-style leg into one <rte> so Garmin devices treat the
    // whole hike as a single navigable route.
    const gpx = geojsonToGpx(featureCollection, null, {
      ...metadata,
      outputType: 'rte',
      singleRoute: true,
      routeName: metadata.metadata?.name || resourceContext.value.name || 'Hike'
    });
    const blob = new Blob([gpx], { type: formatConfig.gpx_garmin.mimeType });
    const garminName = fileName.replace(/\.gpx$/i, '-garmin.gpx');
    triggerDownload(blob, garminName);
    return;
  }

  if (formatId === 'gpx') {
    const gpx = geojsonToGpx(featureCollection, null, {
      ...metadata,
      outputType: 'rte',
      routeName: metadata.metadata?.name || resourceContext.value.name || 'Hike',
      mergeRoutesByProperty: 'hikeDayNumber'
    });
    const blob = new Blob([gpx], { type: formatConfig.gpx.mimeType });
    triggerDownload(blob, fileName);
    return;
  }

  if (formatId === 'geojson') {
    const json = JSON.stringify(featureCollection);
    const blob = new Blob([json], { type: formatConfig.geojson.mimeType });
    const geojsonName = fileName.replace(/\.gpx$/i, `.${formatConfig.geojson.extension}`);
    triggerDownload(blob, geojsonName);
    return;
  }

  if (formatId === 'kml') {
    const kml = lineFeaturesToKml(lineFeatures, metadata.metadata?.name || resourceContext.value.name || 'Trail');
    const blob = new Blob([kml], { type: formatConfig.kml.mimeType });
    const kmlName = fileName.replace(/\.gpx$/i, `.${formatConfig.kml.extension}`);
    triggerDownload(blob, kmlName);
    return;
  }
};

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.DownloadGPX);
  }
};

const handleExportFormat = async (formatId: ExportFormat) => {
  try {
    downloadingFormatId.value = formatId;

    if (!isRecording.value && (formatId === 'gpx' || formatId === 'gpx_garmin' || formatId === 'geojson' || formatId === 'kml')) {
      await downloadTrailViaGraphql(formatId);
      return;
    }

    const downloadUrl = buildDownloadUrl(formatId);

    if (!downloadUrl) {
      alert(`Unable to download this ${resourceNoun.value}. Please try again.`);
      return;
    }

    const response = await fetch(downloadUrl, {
      credentials: 'include',
      headers: {
        Accept: formatConfig[formatId]?.mimeType || 'application/octet-stream'
      }
    });

    const finalUrl = response.url || downloadUrl;

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        alert('Please sign in to download this file.');
        window.location.href = finalUrl;
      } else {
        alert(`We couldn't download this ${resourceNoun.value}. Please try again.`);
      }
      return;
    }

    if (response.redirected && finalUrl.includes('/login')) {
      alert('Please sign in to download this file.');
      window.location.href = finalUrl;
      return;
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('text/html')) {
      console.warn('Received HTML when requesting download, falling back to direct navigation', {
        requestedUrl: downloadUrl,
        finalUrl,
        contentType
      });
      window.location.href = finalUrl;
      return;
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename =
      extractFilenameFromHeader(contentDisposition) || buildFallbackFilename(formatId);

    triggerDownload(blob, filename);
  } catch (error: any) {
    console.error('Failed to download file', error);
    alert(`We couldn't download this ${resourceNoun.value}. Please try again.`);
  } finally {
    downloadingFormatId.value = null;
  }
};
</script>

<style scoped lang="scss">
// Your existing styles go here
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.hiking-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.export-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  h2 {
    font-size: 24px;
    margin-bottom: 32px;
    font-weight: 600;
  }
}

.file-format-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.format-option {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .download-icon {
    color: #f9e547;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #f9e547;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .app-download-section {
    flex-direction: column;
  }

  .hiking-image {
    flex: none;
    height: 200px;
  }
}
</style>
